import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import ProductHeader from '../../molecules/product-detail-header';
import ProductDescriptions from '../../molecules/product-detail-descriptions';
import ProductRestrictions from '../../molecules/product-detail-restrictions';
import ProductBadge from '../../molecules/product-badge';
import ProductAddToCart from '../../tissues/product-details-add-to-cart';
import ProductAddToCartGift from '../../tissues/product-details-add-gift-card';
import ProductProperties from '../../tissues/product-details-properties';

const ProductInformation = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: product-information;
  flex: 1;
`;

const ProductBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 3rem;
  margin-top: -1rem;

  > div {
    margin: 1rem 2rem 0 0;
  }
`;

const ProductDescription = styled(ProductDescriptions)`
  @media (max-width: ${(props) => props.theme.breakpoint.medium}) {
    order: 98;
  }
`;

const ProductDetails = (props) => {
  const [activeVariant, setActiveVariant] = useState(null);
  const [activeVendor, setActiveVendor] = useState(null);

  const getInternationalShipping = () => {
    const id = activeVariant?.prices?.[0]?.vendor_id;
    const stockItem = activeVariant?.stock_items?.filter((item) => item.vendor_id === id);
    return !!stockItem?.[0]?.international_shipping;
  };

  const handleOnVariantChange = (variant) => {
    if (variant) {
      const maisonetteStockId = variant.stock_items.find((x) => x.stock_location_name === 'Maisonette');

      const bestMargin = variant.prices.sort((a, b) => (
        (a.price - a.offer_settings.cost_price > b.price - b.offer_settings.cost_price) ? 1 : -1
      ))[0];

      const vendor = maisonetteStockId?.vendor_id ?? bestMargin?.vendor_id;

      setActiveVariant(variant);
      setActiveVendor(vendor);
    } else {
      setActiveVariant(null);
      setActiveVendor(null);
    }
  };

  const isGiftCard = () => props.product.classifications?.filter((c) => c.taxon.name === 'E-Gift Card').length > 0;

  const getAvailability = () => {
    if (props.product?.variants?.length <= 0) return 'OutOfStock';

    return props.product?.variants?.some(({ in_stock = false }) => in_stock)
      ? 'InStock'
      : 'OutOfStock';
  };

  const onVendorChange = (vendor) => setActiveVendor(vendor);

  return (
    <ProductInformation key={props?.product?.id}>
      <div itemScope itemType="https://schema.org/Product">
        <meta itemProp="name" content={props?.product?.name ?? 'No Name'} />
        <meta itemProp="brand" content={props?.product?.brand ?? 'No brand'} />
        <meta itemProp="description" content={props?.product?.description ?? 'No description'} />
        <meta itemProp="image" content={props?.product?.master?.images[0]?.large_url ?? 'No image'} />
        <meta itemProp="sku" content={props?.product?.master?.sku ?? 'No SKU'} />
        <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
          <meta itemProp="price" content={props?.product?.variants?.[0]?.price ?? props?.product?.master?.price} />
          <meta itemProp="priceCurrency" content="USD" />
          <meta itemProp="availability" content={getAvailability()} />
          <meta itemProp="url" content={process.browser ? global.window.location.href : props?.product?.slug} />
        </div>
      </div>

      {
        props.product?.trends && props.product?.trends.length > 0 && (
          <ProductBadges>
            {
              props.product.trends.map(
                (badge) => <ProductBadge key={badge.type} badge={badge.value} />
              )
            }
          </ProductBadges>
        )
      }

      <ProductHeader
        isQuickshop={props.isQuickshop}
        title={props?.product?.name}
        brand={props?.product?.brand}
        brandSlug={props?.product?.brand_slug}
        prices={props?.product?.variants?.map((v) => v.prices[0]) ?? []}
        noOffers={props?.product?.variants?.every((v) => v.price === null) ?? true}
        variant={activeVariant}
        vendor={activeVendor}
        slug={props?.product?.slug}
      />

      {
        !props.isQuickshop && (activeVariant || isGiftCard()) && (
          <ProductRestrictions
            leadTime={activeVariant?.lead_time}
            finalSale={activeVariant?.prices?.[0]?.final_sale}
            importDuties={getInternationalShipping()}
            isGiftCard={isGiftCard()}
          />
        )
      }

      {
        !props.isQuickshop && (
          <ProductDescription
            brand={props.product.brand}
            brandDescription={props.product.brand_description}
            description={props.product.description}
          />
        )
      }

      {
        isGiftCard() ? (
          <ProductAddToCartGift
            product={props.product}
            variant={activeVariant}
            vendor={activeVendor}
            onVariantChange={handleOnVariantChange}
          />
        ) : (
          <ProductAddToCart
            product={props.product}
            variant={activeVariant}
            vendor={activeVendor}
            onVariantChange={handleOnVariantChange}
            isQuickshop={props.isQuickshop}
          />
        )
      }

      {
        !props.isQuickshop && (
          <ProductProperties
            order={99}
            product={props.product}
            variant={activeVariant}
            vendor={activeVendor}
            onVendorChange={onVendorChange}
          />
        )
      }

    </ProductInformation>
  );
};

ProductDetails.defaultProps = {
  isQuickshop: false
};

ProductDetails.propTypes = {
  product: PropTypes.object.isRequired,
  isQuickshop: PropTypes.bool
};

ProductDetails.whyDidYouRender = true;

export default ProductDetails;
